import React from 'react';
import { useTranslation } from 'react-i18next';
import { EVENT_CATEGORIES, getEventEligibility } from './useBusinessDashboard';
import {
  Calendar, X, Upload, Loader2, CheckCircle, Clock, XCircle,
  Trash2, Edit2, Copy, Eye, MousePointerClick, Send, BellRing, Rocket, RefreshCw, AlertTriangle
} from 'lucide-react';

export const EventsSection = ({
  business, myEvents, eventsLoading, showEventForm, setShowEventForm,
  eventSubmitting, posterUploading, eventForm, setEventForm,
  eventErrors, setEventErrors, editingEventId, setEditingEventId,
  archiveConfirm, setArchiveConfirm, posterInputRef,
  handlePosterUpload, handleEventSubmit, handleEventUpdate,
  openEditForm, handleArchiveEvent, handleDuplicateEvent, fetchMyEvents,
  restoreEvent,
  setBoostModal, setSelectedTier,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState('active');
  const [restoreState, setRestoreState] = React.useState({ busy: null, msg: null });

  // Partition into active (status≠'archived' AND endDate≥now) vs archived (everything else).
  const now = Date.now();
  const isArchivedRow = (e) => {
    if (e.status === 'archived') return true;
    const end = e.endDate ? new Date(e.endDate).getTime() : null;
    return end !== null && end < now;
  };
  const activeEvents = myEvents.filter(e => !isArchivedRow(e));
  const archivedEvents = myEvents.filter(isArchivedRow);
  const visibleEvents = tab === 'archived' ? archivedEvents : activeEvents;

  // Pre-flight: can this business create a new event right now? Mirrors the
  // backend guards so we warn + block BEFORE they fill the form.
  const eligibility = getEventEligibility(business, myEvents);
  const blockedMsg = eligibility.allowed ? null
    : eligibility.reason === 'trialEnded'
      ? t('eventsBlockedTrialEnded', 'Your trial has ended. Add a payment method to publish events again.')
      : eligibility.reason === 'planNoEvents'
        ? t('eventsBlockedPlan', "Your current plan doesn't include events. Upgrade to post events.")
        : t('eventsBlockedMax', { defaultValue: "You've reached your limit of {{max}} active events. Archive an event below or upgrade to Featured for more.", max: eligibility.max });

  const onRestore = async (id) => {
    if (!restoreEvent) return;
    setRestoreState({ busy: id, msg: null });
    const r = await restoreEvent(id);
    setRestoreState({ busy: null, msg: r.ok ? { kind: 'ok', text: t('restored', 'Restored') } : { kind: 'err', text: r.error || t('restoreFailed', 'Could not restore. Please try again.') } });
    if (r.ok) setTab('active');
    setTimeout(() => setRestoreState({ busy: null, msg: null }), 4000);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="my-events-card">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            {t('myEvents')} ({myEvents.length})
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={fetchMyEvents} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <RefreshCw className="h-4 w-4" />
            </button>
            {!showEventForm && eligibility.allowed && (
              <button
                onClick={() => { setEditingEventId(null); setShowEventForm(true); setEventForm(prev => ({ ...prev, location: business?.businessName || '' })); }}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                data-testid="create-event-btn">
                <Calendar className="h-3.5 w-3.5" />
                {t('createEvent')}
              </button>
            )}
          </div>
        </div>

        {/* Pre-flight block notice — shown when the business can't create a new
            event (trial ended / plan / active-event limit). Editing existing
            events stays available. */}
        {!showEventForm && blockedMsg && (
          <div className="m-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="events-blocked-warning">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900" data-testid="events-blocked-message">{blockedMsg}</p>
          </div>
        )}

        {/* Create/Edit Event Form */}
        {showEventForm && (
          <div className="p-4 border-b bg-purple-50/50" data-testid="create-event-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{editingEventId ? t('editEventTitle') : t('newEvent')}</h3>
              <button onClick={() => { setShowEventForm(false); setEditingEventId(null); setEventErrors({}); setEventForm(f => ({ ...f, duplicatedFrom: null })); }} className="p-1.5 hover:bg-gray-200 rounded-lg">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            {!editingEventId && eventForm.duplicatedFrom && (
              <div className="mb-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-800 flex items-center gap-2" data-testid="event-duplicate-banner">
                <Copy className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{t('recreateEventBanner', { defaultValue: 'Recreating a new event from "{{title}}". Set new dates to publish.', title: eventForm.duplicatedFrom })}</span>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventTitle')} *</label>
                <input type="text" value={eventForm.title} onChange={e => setEventForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={t('eventTitlePlaceholder')}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.title ? 'border-red-400' : 'border-gray-300'}`}
                  data-testid="event-title-input" />
                {eventErrors.title && <p className="text-xs text-red-600 mt-1">{eventErrors.title}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventDescription')}</label>
                <textarea value={eventForm.description} onChange={e => setEventForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('eventDescriptionPlaceholder')} rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                  data-testid="event-description-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventCategory')} *</label>
                <select value={eventForm.category} onChange={e => setEventForm(f => ({ ...f, category: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.category ? 'border-red-400' : 'border-gray-300'}`}
                  data-testid="event-category-select">
                  <option value="">{t('selectEventCategory')}</option>
                  {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{t(c.key)}</option>)}
                </select>
                {eventErrors.category && <p className="text-xs text-red-600 mt-1">{eventErrors.category}</p>}
                {eventForm.category === 'other' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={eventForm.customCategory}
                      onChange={e => setEventForm(f => ({ ...f, customCategory: e.target.value }))}
                      placeholder={t('customCategoryPlaceholder', 'Describe the event type (e.g. Car Boot Sale, Pet Show)')}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.customCategory ? 'border-red-400' : 'border-gray-300'}`}
                      data-testid="event-custom-category-input"
                      maxLength={60}
                    />
                    {eventErrors.customCategory && <p className="text-xs text-red-600 mt-1">{eventErrors.customCategory}</p>}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventDate')} *</label>
                  <input type="date" value={eventForm.date} onChange={e => setEventForm(f => ({ ...f, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    max={editingEventId && eventForm.originalEndDate ? new Date(eventForm.originalEndDate).toISOString().split('T')[0] : undefined}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.date ? 'border-red-400' : 'border-gray-300'}`}
                    data-testid="event-date-input" />
                  {eventErrors.date && <p className="text-xs text-red-600 mt-1">{eventErrors.date}</p>}
                  {editingEventId && eventForm.originalEndDate && (
                    <p className="text-xs text-gray-500 mt-1" data-testid="event-edit-date-hint">
                      {t('editDateHint', 'You can keep or move this date earlier — but it cannot be extended past the original end date. Create a new event instead.')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('startTime')} *</label>
                  <input type="time" value={eventForm.startTime} onChange={e => setEventForm(f => ({ ...f, startTime: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.startTime ? 'border-red-400' : 'border-gray-300'}`}
                    data-testid="event-start-time-input" />
                  {eventErrors.startTime && <p className="text-xs text-red-600 mt-1">{eventErrors.startTime}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('endTime')} *</label>
                  <input type="time" value={eventForm.endTime} onChange={e => setEventForm(f => ({ ...f, endTime: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.endTime ? 'border-red-400' : 'border-gray-300'}`}
                    data-testid="event-end-time-input" />
                  {eventErrors.endTime && <p className="text-xs text-red-600 mt-1">{eventErrors.endTime}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventPrice')}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8364;</span>
                    <input type="number" step="0.01" min="0" value={eventForm.price}
                      onChange={e => setEventForm(f => ({ ...f, price: e.target.value }))}
                      placeholder={t('eventPricePlaceholder')}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                      data-testid="event-price-input" />
                  </div>
                  {!eventForm.price && <p className="text-xs text-gray-400 mt-1">{t('free')}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventVenue')} *</label>
                  <input type="text" value={eventForm.location} onChange={e => setEventForm(f => ({ ...f, location: e.target.value }))}
                    placeholder={t('eventVenuePlaceholder')}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none ${eventErrors.location ? 'border-red-400' : 'border-gray-300'}`}
                    data-testid="event-venue-input" />
                  {eventErrors.location && <p className="text-xs text-red-600 mt-1">{eventErrors.location}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('eventPoster')}</label>
                <input ref={posterInputRef} type="file" accept="image/*" onChange={handlePosterUpload} className="hidden" />
                {eventForm.image ? (
                  <div className="flex items-center gap-3">
                    <img src={eventForm.image} alt="Poster" className="h-16 w-16 object-cover rounded-lg border" />
                    <button onClick={() => setEventForm(f => ({ ...f, image: null }))} className="text-sm text-red-600 hover:text-red-800">{t('removePoster')}</button>
                  </div>
                ) : (
                  <button onClick={() => posterInputRef.current?.click()} disabled={posterUploading}
                    className="px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="upload-poster-btn">
                    {posterUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {posterUploading ? t('uploading') : t('uploadPoster')}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <label className="flex items-center gap-3 cursor-pointer group" data-testid="event-notify-toggle">
                  <div className={`relative w-10 h-5 rounded-full transition-colors ${eventForm.notifyFollowers ? 'bg-purple-600' : 'bg-gray-300'}`}
                    onClick={() => setEventForm(f => ({ ...f, notifyFollowers: !f.notifyFollowers }))}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${eventForm.notifyFollowers ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <BellRing className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-gray-700">{t('notifyFollowers')}</span>
                  </div>
                </label>
                <div className="flex items-center gap-3 opacity-50 cursor-not-allowed" data-testid="event-boost-placeholder">
                  <div className="relative w-10 h-5 rounded-full bg-gray-200">
                    <div className="absolute top-0.5 translate-x-0.5 w-4 h-4 bg-white rounded-full shadow" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Rocket className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-400">{t('boostPost')}</span>
                    <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">{t('boostAfterCreate')}</span>
                  </div>
                </div>
              </div>
              {eventErrors.submit && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center" data-testid="event-submit-error">
                  {eventErrors.submit}
                </p>
              )}
              <button onClick={editingEventId ? handleEventUpdate : handleEventSubmit} disabled={eventSubmitting}
                className="w-full py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-event-btn">
                {eventSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {eventSubmitting ? (editingEventId ? t('updatingEvent') : t('creatingEvent')) : (editingEventId ? t('updateEvent') : t('submitEvent'))}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-4 pt-3 border-b border-gray-100 flex gap-2" data-testid="events-tabs">
          <button
            type="button"
            onClick={() => setTab('active')}
            className={`px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors ${tab === 'active' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:text-gray-800'}`}
            data-testid="events-tab-active"
            data-state={tab === 'active' ? 'active' : 'inactive'}
          >
            {t('tabActive', 'Active')} ({activeEvents.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('archived')}
            className={`px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors ${tab === 'archived' ? 'bg-gray-100 text-gray-700' : 'text-gray-500 hover:text-gray-800'}`}
            data-testid="events-tab-archived"
            data-state={tab === 'archived' ? 'active' : 'inactive'}
          >
            {t('tabArchived', 'Archived')} ({archivedEvents.length})
          </button>
        </div>

        {tab === 'active' && (
          <p className="px-4 pt-2 text-xs text-gray-500" data-testid="events-active-hint">
            {t('eventsActiveHint', "Active = live and upcoming events only. Past/expired events don't count against your limit.")}
          </p>
        )}

        <div className="p-4">
          {restoreState.msg && (
            <p className={`mb-2 text-xs ${restoreState.msg.kind === 'ok' ? 'text-green-700' : 'text-red-700'}`} data-testid="events-restore-toast">
              {restoreState.msg.text}
            </p>
          )}
          {eventsLoading ? (
            <div className="text-center py-6 text-gray-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />{t('loadingEvents')}</div>
          ) : visibleEvents.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>{tab === 'archived' ? t('noArchivedItems', 'Nothing archived yet.') : (myEvents.length === 0 ? t('noEventsCreated') : t('noActiveItems', 'Nothing active right now.'))}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleEvents.map((event) => {
                const statusConfig = {
                  pending: { bg: 'bg-yellow-50 border-yellow-200', icon: <Clock className="h-4 w-4 text-yellow-600" />, label: t('eventPending'), textColor: 'text-yellow-700' },
                  approved: { bg: 'bg-green-50 border-green-200', icon: <CheckCircle className="h-4 w-4 text-green-600" />, label: t('eventApproved'), textColor: 'text-green-700' },
                  rejected: { bg: 'bg-red-50 border-red-200', icon: <XCircle className="h-4 w-4 text-red-600" />, label: t('eventRejected'), textColor: 'text-red-700' },
                  expired: { bg: 'bg-gray-50 border-gray-200', icon: <Clock className="h-4 w-4 text-gray-500" />, label: t('eventExpired'), textColor: 'text-gray-600' },
                  archived: { bg: 'bg-gray-50 border-gray-300', icon: <Trash2 className="h-4 w-4 text-gray-400" />, label: t('eventArchived'), textColor: 'text-gray-500' },
                };
                const sc = statusConfig[event.status] || statusConfig.pending;
                const canEdit = event.status !== 'archived' && event.status !== 'expired';
                const canArchive = event.status !== 'archived';
                // Restore allowed only when archived AND endDate is still in the future.
                const isArchivedAndRestorable = (
                  event.status === 'archived' &&
                  event.endDate && new Date(event.endDate).getTime() > now
                );
                return (
                  <div key={event.id} className={`border rounded-lg p-3 ${sc.bg}`} data-testid={`my-event-${event.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{event.title}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{new Date(event.startDate).toLocaleDateString()} - {new Date(event.endDate).toLocaleDateString()}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.textColor} flex-shrink-0 ml-2`}>
                        {sc.icon} {sc.label}
                      </span>
                    </div>
                    {event.status === 'rejected' && event.rejectionReason && (
                      <div className="mt-2 text-xs text-red-700 bg-red-100 rounded p-2"><strong>{t('rejectionReasonLabel')}:</strong> {event.rejectionReason}</div>
                    )}
                    {(event.views > 0 || event.clicks > 0 || event.pushSent > 0 || event.emailSent > 0 || event.status === 'approved') && (
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200/40 text-[11px] text-gray-500" data-testid={`event-analytics-${event.id}`}>
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{event.views || 0}</span>
                        <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{event.clicks || 0}</span>
                        {event.views > 0 && <span className="text-purple-600 font-medium">{Math.round((event.clicks || 0) / event.views * 100)}% CTR</span>}
                        {(event.pushSent > 0 || event.emailSent > 0) && (
                          <span className="flex items-center gap-1"><Send className="h-3 w-3" />{(event.pushSent || 0) + (event.emailSent || 0)} {t('sent')}</span>
                        )}
                        {/* Iter 124: Boost button on approved event cards — mirrors OffersSection */}
                        {event.status === 'approved' && (
                          <div className="ml-auto">
                            {event.boosted ? (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full"
                                data-testid={`event-boosted-badge-${event.id}`}
                              >
                                <Rocket className="h-2.5 w-2.5" />{t('boosted')}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBoostModal({ postId: event.id, postType: 'event', title: event.title });
                                  setSelectedTier('24h');
                                }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded-full hover:bg-purple-200 transition-colors"
                                data-testid={`boost-event-btn-${event.id}`}
                              >
                                <Rocket className="h-2.5 w-2.5" />{t('boostPost')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {(canEdit || canArchive || isArchivedAndRestorable) && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-200/60">
                        {canEdit && (
                          <button onClick={() => openEditForm(event)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 transition-colors"
                            data-testid={`edit-event-btn-${event.id}`}>
                            <Edit2 className="h-3 w-3" />{t('editEvent')}
                          </button>
                        )}
                        {handleDuplicateEvent && (
                          <button onClick={() => handleDuplicateEvent(event)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                            data-testid={`duplicate-event-btn-${event.id}`}
                            title={t('recreateEventTooltip', 'Create a new event from this one — set new dates to publish')}>
                            <Copy className="h-3 w-3" />{t('recreateEvent', 'Recreate')}
                          </button>
                        )}
                        {isArchivedAndRestorable && (
                          <button
                            onClick={() => onRestore(event.id)}
                            disabled={restoreState.busy === event.id}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-md hover:bg-emerald-200 transition-colors disabled:opacity-60"
                            data-testid={`restore-event-btn-${event.id}`}
                          >
                            <RefreshCw className="h-3 w-3" />{restoreState.busy === event.id ? '…' : t('restore', 'Restore')}
                          </button>
                        )}
                        {canArchive && (
                          <button onClick={() => setArchiveConfirm({ id: event.id, title: event.title })}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                            data-testid={`archive-event-btn-${event.id}`}>
                            <Trash2 className="h-3 w-3" />{t('archiveEvent')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {archiveConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('archiveEventConfirm')}</h3>
            <p className="text-sm text-gray-600 mb-1">{t('archiveEventDesc')}</p>
            <p className="font-semibold text-gray-900 mb-4">"{archiveConfirm.title}"</p>
            <div className="flex gap-3">
              <button onClick={() => setArchiveConfirm(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors">{t('cancel')}</button>
              <button onClick={() => handleArchiveEvent(archiveConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-archive-btn">
                <Trash2 className="h-4 w-4" />{t('yesArchive')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
